import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { X, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import GlutenFreeIcon from '@/components/GlutenFreeIcon';

const Cart = () => {
  const { items, isCartOpen, setIsCartOpen, updateQuantity, removeFromCart, totalPrice, totalItems } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate('/checkout');
  };

  const handleOpenCart = () => {
    // Scroll to top of cart panel on mobile
    setTimeout(() => {
      const cartPanel = document.querySelector('[data-cart-panel]');
      if (cartPanel) {
        cartPanel.scrollTop = 0;
      }
    }, 0);
  };

  // Scroll cart to top when opened
  if (!isCartOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50"
        onClick={() => setIsCartOpen(false)}
      />

      {/* Cart Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-card border-l border-border z-50 flex flex-col animate-slide-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold text-foreground">
              Carrello ({totalItems})
            </h2>
          </div>
          <button
            onClick={() => setIsCartOpen(false)}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Chiudi carrello"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ShoppingBag className="h-16 w-16 text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground">Il carrello è vuoto</p>
              <p className="text-sm text-muted-foreground/70 mt-1">
                Aggiungi delle pizze per iniziare!
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map(item => (
                <div key={item.id} className="flex gap-4 p-4 rounded-xl bg-muted/30 border border-border">
                  {/* Pizza emoji as placeholder */}
                  <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">🍕</span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="font-medium text-foreground truncate">
                          {item.pizza.name}
                        </h4>
                        {item.isGlutenFree && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full badge-gf mt-1">
                            <GlutenFreeIcon size={12} />
                            Senza glutine
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-muted-foreground hover:text-destructive transition-colors"
                        aria-label={`Rimuovi ${item.pizza.name} dal carrello`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="flex items-center justify-between mt-3">
                      {/* Quantity controls */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-foreground hover:bg-primary hover:text-primary-foreground transition-colors"
                          aria-label={`Diminuisci quantità ${item.pizza.name}`}
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="w-8 text-center font-medium text-foreground">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-foreground hover:bg-primary hover:text-primary-foreground transition-colors"
                          aria-label={`Aumenta quantità ${item.pizza.name}`}
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>

                      {/* Price */}
                      <span className="font-semibold text-primary">
                        €{(item.pizza.price * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-border p-4 space-y-4">
            <div className="flex items-center justify-between text-lg">
              <span className="text-muted-foreground">Totale:</span>
              <span className="font-bold text-foreground text-xl">
                €{totalPrice.toFixed(2)}
              </span>
            </div>
            <Button variant="hero" className="w-full" size="lg" onClick={handleCheckout}>
              Procedi all'Ordine
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default Cart;
